import { ArrowRightSLine, ArrowTopRightLine } from '@bizzabo/design-system';

import { useStyles } from './DrawerNavigation.style';
import { useDrawerContentAtom, useDrawerStateAtom } from '../../atoms';
import useTypeTracking from '../../../../../analytics';
import { UserProfileMobileCTA } from '../../../../../people/UserProfile/components/UserProfileMobileCTA';
import type { DrawerProps } from '../types';
import { useIsCommunityEnabled } from 'people';
import { DrawerNav } from '../const';
import DrawerLink from '../../Components/DrawerLink';
import DrawerNavLink from '../../Components/DrawerNavLink';
import DrawerDivider from '../../Components/DrawerDivider';
import useAgendaUrl from 'hooks/useVenueUrl/useAgendaUrl';
import useVenueUrl from 'hooks/useVenueUrl/useVenueUrl';
import useIsEventExperienceSummitEvent from 'hooks/useIsEventExperienceSummitEvent';
import {
    IntegrationPointData,
    IntegrationPointType,
} from '../../../../../extensions/types/appExtension';
import useEventAppExtensions from '../../../../../hooks/useEventAppExtensions';
import { useIsAgendaEnabledInEvent } from '../../../../../displayHelpers';

export const DrawerNavigation: React.FC<DrawerProps> = ({
    eventUniqueName,
    eventId,
    goToHomePage,
    menuCustomization,
}) => {
    const classes = useStyles();
    const { setDrawerState } = useDrawerStateAtom();
    const { setDrawerContent } = useDrawerContentAtom();
    const closeNavigation = () => setDrawerState(false);
    const goToSettings = () => setDrawerContent(DrawerNav.MY_SETTINGS_CONTENT);
    const goToUserProfile = () => setDrawerContent(DrawerNav.MY_PROFILE_CONTENT);
    const { trackEvent } = useTypeTracking();
    const isCommunityEnabled = useIsCommunityEnabled(eventUniqueName);
    const agendaUrl = useAgendaUrl();
    const venueUrl = useVenueUrl();
    const isEventExperienceSummitEvent = useIsEventExperienceSummitEvent();
    const shouldShowAgenda = useIsAgendaEnabledInEvent(eventUniqueName);
    const appExtension = useEventAppExtensions(
        eventId,
        IntegrationPointType.VENUE_EVENT_MAIN_STAGE
    );
    const navigationText = {
        agenda: menuCustomization?.agendaText || 'Agenda',
        community: menuCustomization?.communityText || 'Community',
        moreInfo: menuCustomization?.moreInfoText || 'More Info',
    };

    return (
        <nav className={classes.navigation}>
            <UserProfileMobileCTA eventId={eventId} onClick={goToUserProfile} />
            <DrawerDivider />
            <ul>
                {shouldShowAgenda && (
                    <li>
                        <DrawerNavLink
                            to={agendaUrl}
                            onClick={() => {
                                trackEvent({
                                    trackType: 'Click',
                                    data: {
                                        buttonType: 'GO_TO_VIA_PAGE_HEADER',
                                        subCategory: 'AGENDA_RING',
                                    },
                                });
                                closeNavigation();
                            }}
                        >
                            {navigationText.agenda}
                        </DrawerNavLink>
                    </li>
                )}

                {isCommunityEnabled && (
                    <li>
                        <DrawerNavLink
                            to={`${venueUrl}/community`}
                            onClick={() => {
                                closeNavigation();
                            }}
                        >
                            {navigationText.community}
                        </DrawerNavLink>
                    </li>
                )}
                {appExtension && (
                    <li key={appExtension.id}>
                        <DrawerNavLink
                            to={`${venueUrl}/extension/${appExtension.id}`}
                            onClick={closeNavigation}
                        >
                            {IntegrationPointData.VENUE_EVENT_MAIN_STAGE.label}
                        </DrawerNavLink>
                    </li>
                )}
                <li>
                    <DrawerLink onClick={goToHomePage}>
                        {isEventExperienceSummitEvent
                            ? 'Summit Activity Series '
                            : navigationText.moreInfo}
                        <ArrowTopRightLine className={classes.moreInfoIcon} />
                    </DrawerLink>
                </li>
                <li>
                    <DrawerLink className={classes.mySettingsLink} onClick={goToSettings}>
                        My Settings <ArrowRightSLine className={classes.mySettingsIcon} />
                    </DrawerLink>
                </li>
            </ul>
            <DrawerDivider />
        </nav>
    );
};
